import { NavLink, Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CgMenuRight } from "react-icons/cg";
import { RxCross2 } from "react-icons/rx";
import { FiChevronDown,FiUser,FiLogOut,FiSettings,FiShoppingCart,FiCreditCard,FiHome,FiUsers,FiBriefcase,FiMail,} from "react-icons/fi";
import Logo from "../../Assets/LOGO_EDUWEALTH__FINAL.png";
import useVendorStore from "../../AuthStore/store/authStore";

// Animation variants
const menuVariants = {
  open: { x: 0, opacity: 1 },
  closed: { x: "-100%", opacity: 0 },
};

const submenuVariants = {
  open: { opacity: 1, height: "auto", transition: { duration: 0.3 } },
  closed: { opacity: 0, height: 0, transition: { duration: 0.2 } },
};

const dropdownVariants = {
  open: { opacity: 1, scale: 1, y: 0 },
  closed: { opacity: 0, scale: 0.95, y: -10 },
};

const NavBar = () => {
  const { isVendor, setVendor, handleLogOut, authToken, isCustomer, setUser } =
    useVendorStore();
  const navigate = useNavigate();

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hoveredSubmenu, setHoveredSubmenu] = useState(null);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const submenuRefs = useRef({});
  const submenuTimeoutRef = useRef(null);

  // Menu items
  const menuItems = [
    { path: "/", name: "Home", icon: FiHome },
    { path: "/about-us", name: "About Us", icon: FiUsers },
    {
      name: "Services",
      icon: FiBriefcase,
      submenu: [
        { path: "/mutual-funds", name: "Mutual Funds", icon: FiCreditCard },
        { path: "/courses", name: "Courses", icon: FiMail },
      ],
    },
    { path: "/contact", name: "Contact", icon: FiMail },
  ];

  // Effects
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && 
          !mobileMenuRef.current.contains(event.target) && 
          !event.target.closest('[aria-label="Toggle menu"]')) {
        closeAllMenus();
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "unset";
    };
  }, [isOpen]);

  // User helpers
  const getUserInitials = () => {
    const firstName = setVendor?.f_name || setUser?.f_name || "";
    const lastName = setVendor?.l_name || setUser?.l_name || "";
    return (firstName[0] || "") + (lastName[0] || "");
  };

  const getUserName = () =>
    `${setVendor?.f_name || setUser?.f_name || ""} ${
      setVendor?.l_name || setUser?.l_name || ""
    }`.trim();

  const getUserEmail = () => setVendor?.email || setUser?.email || "";
  const getUserRole = () => (isVendor ? "Broker" : "Customer");

  // Navigation helpers with scroll to top
  const handleNavigateWithScroll = (path) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(path);
    closeAllMenus();
  };

  const handleDashboardNavigate = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (isVendor) {
      navigate("/auth/verify/broker-dashbord");
    } else {
      navigate("/auth/verify/customer-dashbord");
    }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Submenu hover handlers
  const handleSubmenuMouseEnter = (menuName) => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
    setHoveredSubmenu(menuName);
    setActiveSubmenu(menuName);
  };

  const handleSubmenuMouseLeave = (menuName) => {
    submenuTimeoutRef.current = setTimeout(() => {
      if (hoveredSubmenu === menuName) {
        setHoveredSubmenu(null);
        setActiveSubmenu(null);
      }
    }, 300); // 300ms delay before closing
  };

  const handleSubmenuClick = (menuName) => {
    setActiveSubmenu(activeSubmenu === menuName ? null : menuName);
    // Clear any pending hover timeouts
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
  };

  const closeAllMenus = () => {
    setIsOpen(false);
    setActiveSubmenu(null);
    setHoveredSubmenu(null);
    setDropdownOpen(false);
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
  };

  // Mobile auth buttons component
  const MobileAuthButtons = () => (
    <div className="lg:hidden border-t border-gray-100 pt-4 mt-4">
      {!isVendor && !isCustomer ? (
        <div className="flex flex-col gap-3 px-2">
          <Link to="/log-in" onClick={closeAllMenus}>
            <button className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg text-base">
              Log In
            </button>
          </Link>
          <Link to="/sign-up" onClick={closeAllMenus}>
            <button className="w-full px-4 py-3 rounded-lg bg-white text-blue-600 font-medium border border-blue-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-300 shadow-sm text-base">
              Sign Up
            </button>
          </Link>
        </div>
      ) : (
        <div className="px-2">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl mb-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-500 text-white font-semibold text-lg">
                {getUserInitials() || <FiUser className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 truncate text-base">
                  {getUserName()}
                </p>
                <p className="text-sm text-gray-600 truncate">
                  {getUserEmail()}
                </p>
                <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full capitalize">
                  {getUserRole()}
                </span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-2 mb-3">
            <button
              onClick={() => { handleDashboardNavigate(); closeAllMenus(); }}
              className="flex flex-col items-center p-3 rounded-lg bg-gray-50 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200"
            >
              <FiUser className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Profile</span>
            </button>
            <button
              onClick={() => { handleNavigateWithScroll("/policy-buy"); }}
              className="flex flex-col items-center p-3 rounded-lg bg-gray-50 hover:bg-green-50 hover:text-green-600 transition-all duration-200"
            >
              <FiShoppingCart className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Get Policy</span>
            </button>
            <button
              onClick={() => { handleDashboardNavigate(); closeAllMenus(); }}
              className="flex flex-col items-center p-3 rounded-lg bg-gray-50 hover:bg-purple-50 hover:text-purple-600 transition-all duration-200"
            >
              <FiSettings className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Dashboard</span>
            </button>
          </div>
          
          <button
            onClick={() => { handleLogOut(authToken); closeAllMenus(); }}
            className="w-full flex items-center justify-center px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 font-medium border border-red-200"
          >
            <FiLogOut className="w-4 h-4 mr-2" />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-lg shadow-sm py-0"
          : "bg-white py-0"
      }`}
    >
      <nav className="flex justify-between items-center w-full px-3 sm:px-4 lg:px-8 mx-auto h-20 sm:h-24">
        {/* Logo */}
        <NavLink
          to="/"
          className="flex items-center group flex-shrink-0"
          onClick={() => {
            closeAllMenus();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <img
            src={Logo}
            alt="KarikarEduWealth"
            className="h-16 w-16 sm:h-20 sm:w-20 md:h-20 md:w-20 transition-transform duration-300 group-hover:scale-105"
          />
          <span className="ml-2 hidden sm:inline text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent whitespace-nowrap">
            KarikarEduWealth
          </span>
        </NavLink>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
          <ul className="flex items-center space-x-1">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <li key={item.path || item.name} className="relative">
                  {item.submenu ? (
                    <div 
                      className="relative"
                      ref={el => submenuRefs.current[item.name] = el}
                      onMouseEnter={() => handleSubmenuMouseEnter(item.name)}
                      onMouseLeave={() => handleSubmenuMouseLeave(item.name)}
                    >
                      <button
                        onClick={() => handleSubmenuClick(item.name)}
                        className="flex items-center gap-2 px-3 xl:px-4 py-2 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 font-medium text-sm xl:text-base"
                      >
                        <IconComponent className="w-4 h-4" />
                        {item.name}
                        <FiChevronDown
                          className={`w-3 h-3 transition-transform duration-300 ${
                            activeSubmenu === item.name ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      
                      {/* Desktop Submenu - Shows on hover and click */}
                      <AnimatePresence>
                        {(activeSubmenu === item.name || hoveredSubmenu === item.name) && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50"
                            onMouseEnter={() => handleSubmenuMouseEnter(item.name)}
                            onMouseLeave={() => handleSubmenuMouseLeave(item.name)}
                          >
                            {item.submenu.map((subItem) => {
                              const SubIconComponent = subItem.icon;
                              return (
                                <NavLink
                                  key={subItem.path}
                                  to={subItem.path}
                                  className={({ isActive }) => `
                                    flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition-all duration-200 font-medium text-sm ${
                                      isActive
                                        ? "bg-blue-50 text-blue-600"
                                        : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                                    }
                                  `}
                                  onClick={() => {
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                    closeAllMenus();
                                  }}
                                >
                                  <SubIconComponent className="w-4 h-4" />
                                  {subItem.name}
                                </NavLink>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <NavLink
                      to={item.path}
                      className={({ isActive }) => `
                        flex items-center gap-2 px-3 xl:px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm xl:text-base ${
                          isActive
                            ? "text-blue-600 bg-blue-50"
                            : "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                        }
                      `}
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        closeAllMenus();
                      }}
                    >
                      <IconComponent className="w-4 h-4" />
                      {item.name}
                    </NavLink>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Auth Buttons inline */}
          <div className="ml-2">
            {!isVendor && !isCustomer ? (
              <div className="flex items-center gap-2 sm:gap-3">
                <Link to="/log-in">
                  <button 
                    className="px-4 py-2 sm:px-6 sm:py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  >
                    Log In
                  </button>
                </Link>
                <Link to="/sign-up">
                  <button 
                    className="px-4 py-2 sm:px-6 sm:py-2.5 rounded-lg bg-white text-blue-600 font-medium border border-blue-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-300 shadow-sm hover:shadow-md text-sm sm:text-base"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  >
                    Sign Up
                  </button>
                </Link>
              </div>
            ) : (
              <div className="relative" ref={dropdownRef}>
                {/* Profile Dropdown */}
                <button
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className="flex items-center gap-2 p-2 sm:px-3 sm:py-2 rounded-xl bg-white border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md transition-all duration-300 group"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold text-sm min-w-[32px]">
                    {getUserInitials() || <FiUser className="w-4 h-4" />}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="font-medium text-gray-800 text-sm leading-tight">
                      {getUserName() || "Welcome"}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">
                      {getUserRole()}
                    </p>
                  </div>
                  <FiChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${
                      dropdownOpen
                        ? "rotate-180 text-blue-500"
                        : "group-hover:text-gray-600"
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      variants={dropdownVariants}
                      initial="closed"
                      animate="open"
                      exit="closed"
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-72 max-w-[90vw] sm:max-w-none bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
                    >
                      {/* User Info */}
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 border-b border-blue-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-blue-500 text-white font-semibold">
                            {getUserInitials() || (
                              <FiUser className="w-4 h-4 sm:w-5 sm:h-5" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-800 truncate text-sm sm:text-base">
                              {getUserName()}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-600 truncate">
                              {getUserEmail()}
                            </p>
                            <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full capitalize">
                              {getUserRole()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="p-3 border-b border-gray-100">
                        <div className="grid grid-cols-3 gap-1 sm:gap-2">
                          <button
                            onClick={handleDashboardNavigate}
                            className="flex flex-col items-center p-2 rounded-lg bg-gray-50 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200"
                          >
                            <FiUser className="w-4 h-4 mb-1" />
                            <span className="text-xs font-medium">Profile</span>
                          </button>
                          <button
                            onClick={() => handleNavigateWithScroll("/policy-buy")}
                            className="flex flex-col items-center p-2 rounded-lg bg-gray-50 hover:bg-green-50 hover:text-green-600 transition-all duration-200"
                          >
                            <FiShoppingCart className="w-4 h-4 mb-1" />
                            <span className="text-xs font-medium">Policies</span>
                          </button>
                          <button
                            onClick={() => handleNavigateWithScroll("/account-settings")}
                            className="flex flex-col items-center p-2 rounded-lg bg-gray-50 hover:bg-purple-50 hover:text-purple-600 transition-all duration-200"
                          >
                            <FiSettings className="w-4 h-4 mb-1" />
                            <span className="text-xs font-medium">Settings</span>
                          </button>
                        </div>
                      </div>

                      {/* Logout */}
                      <div className="border-t border-gray-100 p-3 bg-gray-50">
                        <button
                          onClick={() => handleLogOut(authToken)}
                          className="flex items-center justify-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 font-medium"
                        >
                          <FiLogOut className="w-4 h-4 mr-2" />
                          Sign Out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="flex lg:hidden items-center gap-2">
          {!isVendor && !isCustomer ? (
            <div className="flex items-center gap-2 mr-2">
              <Link to="/log-in">
                <button 
                  className="px-5 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg text-sm"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                >
                  Log In
                </button>
              </Link>
            </div>
          ) : (
            <div className="mr-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold text-sm">
                {getUserInitials() || <FiUser className="w-4 h-4" />}
              </div>
            </div>
          )}
          
          <button
            onClick={handleToggle}
            className="p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors duration-200 flex items-center justify-center w-10 h-10"
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <RxCross2 className="text-lg text-blue-600" />
            ) : (
              <CgMenuRight className="text-xl text-blue-600" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 z-40 lg:hidden"
              onClick={closeAllMenus}
            />
            <motion.div
              ref={mobileMenuRef}
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="fixed top-0 left-0 w-80 max-w-[85vw] h-screen bg-white flex flex-col z-50 shadow-xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
                <NavLink
                  to="/"
                  onClick={() => {
                    closeAllMenus();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="flex items-center"
                >
                  <img
                    src={Logo}
                    alt="KarikarEduWealth"
                    className="h-10 w-10"
                  />
                  <span className="ml-2 text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                    KarikarEduWealth
                  </span>
                </NavLink>
                <button
                  onClick={closeAllMenus}
                  className="p-2 rounded-lg bg-white hover:bg-gray-100 transition-colors duration-200 shadow-sm flex items-center justify-center w-10 h-10"
                  aria-label="Close menu"
                >
                  <RxCross2 className="text-lg text-gray-600" />
                </button>
              </div>

              {/* Mobile Menu Items */}
              <nav className="flex-1 overflow-y-auto p-4">
                <ul className="space-y-1">
                  {menuItems.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <li key={item.path || item.name}>
                        {item.submenu ? (
                          <div className="mb-2">
                            <button
                              onClick={() => handleSubmenuClick(item.name)}
                              className="flex items-center justify-between w-full px-3 py-3 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 font-medium text-base"
                            >
                              <div className="flex items-center gap-3">
                                <IconComponent className="w-5 h-5" />
                                <span>{item.name}</span>
                              </div>
                              <FiChevronDown
                                className={`w-4 h-4 transition-transform duration-300 ${
                                  activeSubmenu === item.name ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                            <AnimatePresence>
                              {activeSubmenu === item.name && (
                                <motion.ul
                                  variants={submenuVariants}
                                  initial="closed"
                                  animate="open"
                                  exit="closed"
                                  className="ml-8 mt-1 space-y-1"
                                >
                                  {item.submenu.map((subItem) => {
                                    const SubIconComponent = subItem.icon;
                                    return (
                                      <li key={subItem.path}>
                                        <NavLink
                                          to={subItem.path}
                                          onClick={() => {
                                            window.scrollTo({ top: 0, behavior: "smooth" });
                                            closeAllMenus();
                                          }}
                                          className={({ isActive }) => `
                                            flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 font-medium text-sm ${
                                              isActive
                                                ? "bg-blue-50 text-blue-600 border-l-2 border-blue-600"
                                                : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                                            }
                                          `}
                                        >
                                          <SubIconComponent className="w-4 h-4" />
                                          {subItem.name}
                                        </NavLink>
                                      </li>
                                    );
                                  })}
                                </motion.ul>
                              )}
                            </AnimatePresence>
                          </div>
                        ) : (
                          <NavLink
                            to={item.path}
                            onClick={() => {
                              window.scrollTo({ top: 0, behavior: "smooth" });
                              closeAllMenus();
                            }}
                            className={({ isActive }) => `
                              flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 font-medium text-base ${
                                isActive
                                  ? "bg-blue-50 text-blue-600"
                                  : "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                              }
                            `}
                          >
                            <IconComponent className="w-5 h-5" />
                            {item.name}
                          </NavLink>
                        )}
                      </li>
                    );
                  })}
                </ul>
                
                {/* Mobile Auth Section */}
                <MobileAuthButtons />
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NavBar;